import { useState } from 'react'

import { FvButton } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'
import { formatMoney } from '@fv/models/core'

import { ListItem } from '@/components/List'
import { useAccountFeatures } from '@/features/auth/hooks'

import { useComparableLoads } from './api'
import { ComparableLoadsPanel } from './ComparableLoadsPanel'

const formatCostPerMile = (amount: number) => `${formatMoney(amount, 'usd')}/mi`

type Props = {
  load: FullShipment
  layout?: 'inline' | 'list-item'
}

const ComparableLoadsContents = ({ load, layout = 'inline' }: Props) => {
  const [isKnobPanelOpen, setKnobPanelOpen] = useState(false)
  const query = useComparableLoads(load.loadId)

  const result = query.data
  const avgCPM = result?.account.costPerMileAverage
  const showTrigger = !query.isLoading && !query.isError
  const display =
    result?.account.count > 0 && avgCPM != null
      ? formatCostPerMile(avgCPM)
      : 'N/A'

  const content = (
    <>
      {showTrigger && (
        <div>
          <span className="w-full">
            Historical pricing:{' '}
            <FvButton theme="underlined" onClick={() => setKnobPanelOpen(true)}>
              {display}
            </FvButton>
          </span>
        </div>
      )}

      <ComparableLoadsPanel
        load={load}
        onClose={() => setKnobPanelOpen(false)}
        isOpen={isKnobPanelOpen}
      />
    </>
  )

  return layout === 'list-item' ? <ListItem>{content}</ListItem> : content
}

type FeatureProps = {
  load?: FullShipment
  layout?: 'inline' | 'list-item'
}

export const ComparableLoadsInline = ({
  load,
  layout = 'inline',
}: FeatureProps) => {
  const { fuelAndDistance } = useAccountFeatures()
  if (
    !load ||
    !fuelAndDistance ||
    load.workflow !== 'truckload' ||
    !load.distance
  ) {
    return null
  }

  return <ComparableLoadsContents load={load} layout={layout} />
}
